import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HealthBadge } from "./operations-view-utils";
import { AttentionState, formatStatusLabel } from "./operations-shared";

interface WorkflowMetric {
  label: string;
  value: string;
}

interface WorkflowSummaryCardProps {
  title: string;
  description?: string;
  status?: string;
  health?: AttentionState;
  healthReason?: string;
  metrics: WorkflowMetric[];
  footer?: ReactNode;
}

export function WorkflowSummaryCard(props: Readonly<WorkflowSummaryCardProps>) {
  const { title, description, status, health, healthReason, metrics, footer } = props;

  return (
    <Card className="border-border/70">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {status ? (
              <span className="rounded-md border px-2.5 py-1 text-xs font-medium">
                {formatStatusLabel(status)}
              </span>
            ) : null}
            {health ? <HealthBadge state={health} reason={healthReason} /> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => (
            <div key={metric.label} className="rounded-lg border bg-muted/30 p-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {metric.label}
              </div>
              <div className="mt-1 text-sm font-semibold">{metric.value}</div>
            </div>
          ))}
        </div>
        {footer}
      </CardContent>
    </Card>
  );
}
